import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

// material-ui
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Portal from '@mui/material/Portal';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';

// project imports
import {
  getDiary,
  saveDiaryDay,
  saveDiaryNote,
  replyToNote,
  resolveNote,
  addHomeActivity,
  getAssignableTargets,
  tickHomeActivity,
  deleteHomeActivity,
  reportConcern,
  replyToConcern,
  closeConcern
} from 'services/api/communicationDiary';
import usePageAccess from 'hooks/usePageAccess';

// ==============================|| CHILD DETAIL - COMMUNICATION DIARY ||============================== //

/**
 * The shared record between a child's care team and their home, as a diary.
 *
 * A spiral-bound notebook, one volume per IEP, one page a day — the presentation from
 * knowledge/html mockups/Admin Persona/Ayro_Communication_Diary_Mockup_2.html rather
 * than the platform's card-and-field furniture. That is a deliberate exception to the
 * house style: this screen is read by parents at home, not by clinicians at a console,
 * and the paper metaphor is what makes a shared record feel shared.
 *
 * AUTHORSHIP IS CARRIED BY INK, which is the whole idea:
 *   ink (navy)   the therapist        green   the supervisor
 *   pencil grey  the caregiver        cream   the activities for home
 * Chrome is sans; anything WRITTEN on the page is serif. Keeping that split is what
 * separates the diary from a form — if a note renders in the sans UI face it stops
 * reading as handwriting and the effect collapses.
 *
 * WHO MAY WRITE WHAT IS NOT DECIDED HERE. The server sends `can` on every read and this
 * screen only reflects it, so the rules can be settled in one place
 * (api/v1/communication_diary.py `_capabilities`) without touching the UI.
 *
 * The palette is FIXED rather than theme-derived. Paper is paper in either theme, and
 * every colour below is stated explicitly so nothing inherits an unreadable value from
 * a dark palette.
 */

const PAPER = {
  desk: '#DFE3DD',
  desk2: '#CCD2C7',
  paper: '#FCFCF9',
  paper2: '#F5F5F0',
  edge: '#E6E4D9',
  hair: '#E8E6DB',
  rule: '#D9A2A6',
  ink: '#1B3557',
  green: '#1C6049',
  pencil: '#4E5054',
  amber: '#8A5A12',
  slip: '#FBF5E4',
  slipEdge: '#E7DBB6',
  grey: '#6E7175',
  grey2: '#93979C',
  serif: '"Iowan Old Style","Palatino Linotype",Palatino,"Book Antiqua",Georgia,serif',
  ringEdge: '#A6ABA2'
};

const VOICES = ['therapist', 'supervisor', 'caregiver'];

/* Long enough to read as paper rather than as a transition. The mockup turns in 620ms with
 * a curve that leaves fast and lands slowly; a little longer, with a softer start, reads as
 * paper being moved by a hand rather than snapped. The timer that clears the leaf, and the
 * shading that crosses the page under it, are both tied to this. */
const TURN_MS = 700;
const TURN_EASE = 'cubic-bezier(.38,.05,.26,1)';

const todayISO = () => {
  // Local date, NOT toISOString() — that converts to UTC first, so anyone east of
  // Greenwich writing in the evening would file the page under tomorrow.
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** The day `delta` days from an ISO date, kept in local time.
 *
 * Built through the Date constructor's day argument rather than by arithmetic on the
 * date string, so month ends and DST changes land on the day a calendar would show.
 */
const shiftDay = (iso, delta) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dd = new Date(y, m - 1, d + delta);
  const pad = (n) => String(n).padStart(2, '0');
  return `${dd.getFullYear()}-${pad(dd.getMonth() + 1)}-${pad(dd.getDate())}`;
};

// The spiral. Fixed furniture down the left of every page.
const RINGS = Array.from({ length: 13 });

/* ---- the two summary readings of the diary --------------------------------------------
 *
 * Overdue and Attention are filtered readings of the pages already in hand, computed here
 * rather than on the server so the counts on the tabs and the lists behind them are always
 * in step with what the reader is looking at. Each surfaces the ITEMS themselves:
 *
 *   Overdue     the home activities left unticked once their day has passed
 *               (>= OVERDUE_AFTER_DAYS old) — the chores, not the pages.
 *   Attention   the notes still waiting on a reply: a question one side wrote that the other
 *               side has not answered — the caregiver's doubt the team has yet to address,
 *               or a clinical question the family has not.
 *
 * The per-page booleans (isOverdue / isUnanswered) drive the marks worn on each leaf.
 */
// The day after: a chore is overdue once its day has passed and the box is still unticked.
const OVERDUE_AFTER_DAYS = 1;

const daysAgo = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const then = new Date(y, m - 1, d);
  const [ty, tm, td] = todayISO().split('-').map(Number);
  const today = new Date(ty, tm - 1, td);
  return Math.round((today - then) / 86400000);
};

// Only the caregiver's own chores count — assigned targets are no longer shown or tickable.
const isActivityOverdue = (activity, day) =>
  activity.source !== 'target' && !activity.is_done && daysAgo(day.entry_date) >= OVERDUE_AFTER_DAYS;
const overdueActivitiesOf = (day) => (day?.home_activities || []).filter((a) => isActivityOverdue(a, day));
const isOverdue = (day) => overdueActivitiesOf(day).length > 0;

// The diary has two sides: the home (the caregiver) and the clinic (therapist, supervisor).
const isHomeVoice = (voice) => voice === 'caregiver';

// A caregiver's QUESTION is what waits on a reply — she ticks "ask as a question" as she
// writes (carried on is_question). Like a concern, it stays flagged until it is explicitly
// resolved (Mark closed → is_question flipped false), NOT the moment someone replies: the
// clinic answers in the question's own thread and closes it when the matter is settled.
const noteAwaitsReply = (note) => isHomeVoice(note.author_role_code) && Boolean(note.is_question);
const awaitingNotesOf = (day) => (day?.notes || []).filter((n) => noteAwaitsReply(n));
const isUnanswered = (day) => awaitingNotesOf(day).length > 0;

// A concern stays flagged — and on the Attention list — until a clinician closes it.
const openConcernsOf = (day) => (day?.concerns || []).filter((c) => c.status !== 'closed');

// The kinds and severities a concern can carry — the closed sets the server validates, in
// the order they read in the pickers. Labels are localised; these are the stored codes.
const CONCERN_TYPES = ['injury', 'behaviour', 'medical', 'safeguarding', 'other'];
const CONCERN_SEVERITIES = ['critical', 'urgent', 'awareness'];

// The bell that stands for "needs attention", on the tab and above its list.
const BELL_ICON = (
  <svg viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </svg>
);

export default function CommunicationDiaryTab({ childId, onClose }) {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useCallback((id, values) => intl.formatMessage({ id }, values), [intl]);

  /* AS A TAB, or AS AN OVERLAY.
   *
   * With no `onClose` the diary IS the page: it owns the route, and closing means walking
   * back up to the child profile. Given one, it has been opened ON TOP of something that
   * is still there behind it — a session dialog, a data-collection screen — and closing
   * means handing control back to whoever opened it, not navigating.
   *
   * The one thing overlay mode has to change is height in the stack: it must clear MUI's
   * modals (1300) to cover a dialog, which in turn puts it above its own Select menus
   * (also 1300) unless they are lifted with it. Hence MENU_Z, passed to every menu this
   * screen opens. */
  const overlay = Boolean(onClose);
  const menuZ = overlay ? 1400 : undefined;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [days, setDays] = useState([]);
  const [volumes, setVolumes] = useState([]);
  const [canRaw, setCan] = useState({});
  // Full-Read on the Communication Diary (the feature) or the child's diary page: read the
  // diary but change NOTHING. The `can` object from the API drives every write affordance
  // (write note, worked-on, home activities, replies, report/close concern), so emptying it
  // turns the whole tab read-only — it already renders a read-only notice when `!can.note`.
  // The backend also refuses these writes (the /communication-diary feature gate).
  const { readOnly: isCodeReadOnly } = usePageAccess();
  const diaryReadOnly = isCodeReadOnly('communication_diary') || isCodeReadOnly('child_communication_diary');
  const can = useMemo(() => (diaryReadOnly ? {} : canRaw), [diaryReadOnly, canRaw]);
  // Who the diary is about — { name, center_name } — for the masthead over the page.
  const [child, setChild] = useState(null);
  // The volume in view. There is no "all volumes" reading any more — a diary is always
  // one IEP at a time — so this holds a real volume id. It starts null and is set to the
  // current volume the moment the volumes land (the effect below), and is left alone on a
  // save-driven refresh as long as the chosen volume still exists.
  const [volumeId, setVolumeId] = useState(null);
  // Which reading of the diary is on screen: the book itself, or one of the two summary
  // lists. The lists filter the same pages the book turns through.
  const [view, setView] = useState('day');
  const [activeDate, setActiveDate] = useState(todayISO());
  const [editing, setEditing] = useState(false);
  const [draftNote, setDraftNote] = useState('');
  const [draftActivity, setDraftActivity] = useState('');
  const [settleKey, setSettleKey] = useState(0);
  const [turning, setTurning] = useState(null);
  // Reporting a concern (the red pen), and answering a concern OR a caregiver's question.
  // `replyTo` holds { kind: 'concern' | 'note', id } for the thing being answered, or null.
  const [concernOpen, setConcernOpen] = useState(false);
  const CONCERN_BLANK = { concern_type: 'injury', severity: 'urgent', body: '', action_taken: '', response_needed: true };
  const [concernForm, setConcernForm] = useState(CONCERN_BLANK);
  const [replyTo, setReplyTo] = useState(null);
  const [replyDraft, setReplyDraft] = useState('');

  /* The date picker is the diary's own month calendar, not the browser's — a dropped
   * sheet showing the days that HAVE pages (a pip under each, red when that day still
   * wants attention), the way the mockup does it. `calOpen` is whether it is dropped,
   * `calMonth` the {y, m} of the month on show; both are set fresh each time it opens. */
  const [calOpen, setCalOpen] = useState(false);
  const [calMonth, setCalMonth] = useState(null);
  const calRef = useRef(null);

  // The child's active-IEP targets, for the "worked on" picker: the therapist records
  // which of them the session covered. `pickWorked` holds what is selected in the picker
  // before it is added to the page.
  const [assignable, setAssignable] = useState([]);
  const [pickWorked, setPickWorked] = useState([]);

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await getDiary(childId);
      setDays(data?.days || []);
      setVolumes(data?.volumes || []);
      setCan(data?.can || {});
      setChild(data?.child || null);
      return data;
    } catch (err) {
      const status = err?.response?.status;
      setError(
        status === 403
          ? err?.response?.data?.detail || t('childDetail.diary.errForbidden')
          : err?.response?.data?.detail || err?.message || t('childDetail.diary.errLoad')
      );
      return null;
    } finally {
      setLoading(false);
    }
  }, [childId, t]);

  useEffect(() => {
    load();
  }, [load]);

  // Settle on a volume once they arrive: keep the reader's choice if it still exists,
  // otherwise open on the current IEP (falling back to the newest). Runs on every volumes
  // change so a save-driven refresh does not knock the reader off the volume they picked.
  useEffect(() => {
    if (!volumes.length) return;
    setVolumeId((cur) => {
      if (cur && volumes.some((v) => v.iep_version_id === cur)) return cur;
      return (volumes.find((v) => v.is_active) || volumes[0]).iep_version_id;
    });
  }, [volumes]);

  const visibleDays = useMemo(() => days.filter((d) => d.iep_version_id === volumeId), [days, volumeId]);

  // Pull the child's assignable targets only for a THERAPIST — they alone choose what was
  // worked on, and the route 403s everyone else, so it is only ever called for them.
  useEffect(() => {
    if (!can.set_worked_on) return undefined;
    let alive = true;
    getAssignableTargets(childId)
      .then((rows) => alive && setAssignable(Array.isArray(rows) ? rows : []))
      .catch(() => alive && setAssignable([]));
    return () => {
      alive = false;
    };
  }, [can.set_worked_on, childId]);

  // The volume in view, resolved for the header and the day-line label.
  const volumeById = useMemo(() => new Map(volumes.map((v) => [v.iep_version_id, v])), [volumes]);

  // Newest day first, the way both lists read.
  const byDayDesc = (a, b) => (a.day.entry_date < b.day.entry_date ? 1 : -1);

  // The Overdue list is the overdue home activities THEMSELVES, each tied back to its day.
  const overdueItems = useMemo(() => {
    const out = [];
    visibleDays.forEach((day) => overdueActivitiesOf(day).forEach((activity) => out.push({ day, activity })));
    return out.sort(byDayDesc);
  }, [visibleDays]);

  // The Attention list gathers everything on the page that is waiting on someone: notes that
  // have not been replied to, home activities gone overdue, and concerns still open. Each is a
  // typed row so the list can render them side by side, newest day first.
  const attentionItems = useMemo(() => {
    const out = [];
    visibleDays.forEach((day) => {
      openConcernsOf(day).forEach((concern) => out.push({ kind: 'concern', day, concern }));
      awaitingNotesOf(day).forEach((note) => out.push({ kind: 'note', day, note }));
      overdueActivitiesOf(day).forEach((activity) => out.push({ kind: 'overdue', day, activity }));
    });
    return out.sort(byDayDesc);
  }, [visibleDays]);

  // The numbers on the two tabs, over the volume in view.
  const counts = useMemo(() => ({ overdue: overdueItems.length, attention: attentionItems.length }), [overdueItems, attentionItems]);

  // The outstanding-work marks for a page: worn on the leaf itself and on each list row.
  const marksFor = useCallback(
    (day) => {
      const m = [];
      openConcernsOf(day).forEach((c) =>
        m.push({
          tone: 'red',
          label:
            t(`childDetail.diary.concernType.${c.concern_type}`) + (c.response_needed ? ` · ${t('childDetail.diary.responseNeeded')}` : '')
        })
      );
      if (isOverdue(day)) m.push({ tone: 'amber', label: t('childDetail.diary.markOverdue') });
      if (isUnanswered(day)) m.push({ tone: 'amber', label: t('childDetail.diary.markUnanswered') });
      if (!m.length && day?.session_slot) m.push({ tone: 'calm', label: t('childDetail.diary.markNothing') });
      return m;
    },
    [t]
  );

  const page = useMemo(() => visibleDays.find((d) => d.entry_date === activeDate) || null, [visibleDays, activeDate]);

  const writtenDates = useMemo(() => visibleDays.map((d) => d.entry_date), [visibleDays]);
  const posInWritten = writtenDates.indexOf(activeDate);

  // How far forward the paper may be turned: today, unless the diary already holds a page
  // dated later than today. Backwards has no floor — a diary always has a leaf behind the
  // one you are on, blank or not.
  const lastDate = useMemo(() => writtenDates.reduce((m, d) => (d > m ? d : m), todayISO()), [writtenDates]);

  /* The masthead over the page reads "<child> · <IEP> <period> · <centre>". The IEP it
   * names is the volume in VIEW — a specific one when picked, otherwise the active volume
   * (or the most recent). Its period and the centre are added only when known, so a volume
   * with no dates, or a child with no centre, drops that piece rather than showing an empty
   * separator. The child name and centre come off the diary response; the volume and its
   * dates are already in hand. */
  const headerVolume = useMemo(
    () => volumeById.get(volumeId) || volumes.find((v) => v.is_active) || volumes[0] || null,
    [volumeById, volumeId, volumes]
  );

  const mastSubtitle = useMemo(() => {
    const monthYear = (iso) =>
      iso ? new Date(`${iso}T00:00:00`).toLocaleDateString(intl.locale, { month: 'short', year: 'numeric' }) : '';
    const parts = [];
    if (headerVolume) {
      const from = monthYear(headerVolume.from_date);
      const to = monthYear(headerVolume.to_date);
      parts.push(from && to ? `${headerVolume.label} · ${t('childDetail.diary.iepPeriod', { from, to })}` : headerVolume.label);
    }
    if (child?.center_name) parts.push(child.center_name);
    return parts.join(' · ');
  }, [headerVolume, child, intl.locale, t]);

  /* Open on the most recent written page when today's is blank — ONCE, on the first load.
   * Running this on every render is what made the date field and the arrows look broken:
   * any day without a page was snapped straight back to the latest written one, so the
   * diary appeared stuck on a single date. */
  const openedAt = useRef(false);
  useEffect(() => {
    if (loading || openedAt.current) return;
    // Hold until a volume is chosen: writtenDates is scoped to the volume in view, so
    // running before the volumes effect settles volumeId would read an empty book and
    // mark the one-shot done without ever positioning on the latest page.
    if (!volumeId) return;
    openedAt.current = true;
    if (!page && writtenDates.length) setActiveDate(writtenDates[0]);
  }, [loading, page, writtenDates, volumeId]);

  const noteOf = useCallback((voice) => (page?.notes || []).find((n) => n.author_role_code === voice) || null, [page]);
  const myNote = useMemo(() => (can.voice ? noteOf(can.voice) : null), [can.voice, noteOf]);

  useEffect(() => {
    setDraftNote(myNote?.body || '');
    setEditing(false);
  }, [myNote, page]);

  const dayFor = useCallback((iso) => visibleDays.find((d) => d.entry_date === iso) || null, [visibleDays]);

  /* Turn to another day, and TURN THE PAPER to get there.
   *
   * `dir` is the direction of travel through the volume: +1 later, -1 earlier. It decides
   * which way the leaf swings, so it is derived from the dates rather than passed in by
   * each caller — picking 3 August from the date field while reading 20 August turns the
   * paper backwards, the way reaching for that page would.
   *
   * Anyone who has asked for less motion gets the page swapped under them instead, on the
   * short settle: the leaf still re-mounts, so the change is not silent. */
  const goTo = (iso) => {
    if (!iso) return;
    // Turning to a day is always a return to the book — from either summary list, picking a
    // date (or a row) is a request to read that page.
    setView('day');
    if (iso === activeDate) return;
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (still) {
      setSettleKey((k) => k + 1);
    } else {
      setTurning({ dir: iso > activeDate ? 1 : -1, from: activeDate });
    }
    setActiveDate(iso);
  };

  /* ---- the diary's own month calendar --------------------------------------------------
   *
   * A dropped sheet of one month, MONDAY-FIRST like the mockup, marking only the days that
   * hold a page (a pip under each, red when that day still wants attention). Opening it
   * always shows the month the reader is on; the arrows walk months without leaving the
   * page being read. Picking a marked day turns to it. */
  const openCal = () => {
    const [y, m] = activeDate.split('-').map(Number);
    setCalMonth({ y, m: m - 1 });
    setCalOpen(true);
  };
  const toggleCal = () => (calOpen ? setCalOpen(false) : openCal());
  const stepMonth = (delta) =>
    setCalMonth((c) => {
      const d = new Date(c.y, c.m + delta, 1);
      return { y: d.getFullYear(), m: d.getMonth() };
    });
  const pickDate = (iso) => {
    setCalOpen(false);
    goTo(iso);
  };

  // A day wants attention when something on it is overdue or a note is still unanswered —
  // the same reading the Attention list is built from. It is what turns a pip red.
  const dayNeedsAttention = useCallback((day) => isOverdue(day) || isUnanswered(day), []);

  // Localised Monday-first weekday initials. 2024-01-01 is a Monday, so seven days from it
  // give the header in the reader's own language without hard-coding English.
  const weekdayHeads = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(2024, 0, 1 + i).toLocaleDateString(intl.locale, { weekday: 'short' })),
    [intl.locale]
  );

  // The cells of the month on show: leading blanks to line the 1st up under its weekday,
  // then a cell per day carrying the page (if any) that falls on it.
  const calCells = useMemo(() => {
    if (!calMonth) return [];
    const { y, m } = calMonth;
    const byDate = {};
    visibleDays.forEach((d) => {
      byDate[d.entry_date] = d;
    });
    const lead = (new Date(y, m, 1).getDay() + 6) % 7; // Monday-first: Sunday(0) shifts to the end
    const total = new Date(y, m + 1, 0).getDate();
    const pad = (n) => String(n).padStart(2, '0');
    const cells = [];
    for (let i = 0; i < lead; i += 1) cells.push(null);
    for (let n = 1; n <= total; n += 1) {
      const iso = `${y}-${pad(m + 1)}-${pad(n)}`;
      cells.push({ n, iso, day: byDate[iso] || null });
    }
    return cells;
  }, [calMonth, visibleDays]);

  const calMonthLabel = useMemo(
    () => (calMonth ? new Date(calMonth.y, calMonth.m, 1).toLocaleDateString(intl.locale, { month: 'long', year: 'numeric' }) : ''),
    [calMonth, intl.locale]
  );

  // The two shortcuts under the grid: jump to the newest written page, or to the earliest
  // day in this volume still wanting attention.
  const latestWritten = useMemo(() => (writtenDates.length ? writtenDates.reduce((a, b) => (b > a ? b : a)) : null), [writtenDates]);
  const firstFlaggedDate = useMemo(() => {
    const flagged = visibleDays.filter(dayNeedsAttention).map((d) => d.entry_date);
    return flagged.length ? flagged.reduce((a, b) => (b < a ? b : a)) : null;
  }, [visibleDays, dayNeedsAttention]);
  const goLatestPage = () => {
    if (latestWritten) pickDate(latestWritten);
  };
  const goFirstFlagged = () => {
    if (firstFlaggedDate) {
      pickDate(firstFlaggedDate);
    } else {
      setCalOpen(false);
      setToast({ severity: 'info', msg: t('childDetail.diary.nothingFlagged') });
    }
  };

  /* The leaf in flight is transient furniture: it lives for one animation and is then
   * taken off the stage. Clearing it on a timer rather than on animationend because a
   * turn interrupted by another turn (someone flipping quickly) must not leave the
   * previous leaf standing — a fresh `turning` object restarts this effect and cancels
   * the timer that belonged to the leaf it replaced. */
  useEffect(() => {
    if (!turning) return undefined;
    // A frame or two of slack past the animation. Clearing at exactly TURN_MS could land
    // before the final composite, and the leaf then vanished a hair early — a flick right
    // at the end of an otherwise smooth turn.
    const timer = setTimeout(() => setTurning(null), TURN_MS + 60);
    return () => clearTimeout(timer);
  }, [turning]);

  // Which day each of the two leaves is showing. Turning to a LATER page lifts the page
  // you are on and flips it away, so the stage below already shows where you are going;
  // turning back brings the earlier leaf down over the page you are leaving, so the stage
  // has to keep showing that one until the paper has landed.
  const stageDate = turning && turning.dir < 0 ? turning.from : activeDate;
  const flyingDate = turning ? (turning.dir > 0 ? turning.from : activeDate) : null;

  /* Changing volume carries the reader to that volume's latest page unless the day they
   * are on also exists in it. Left on a date the new volume never covered, the leaf came
   * up blank with no hint that its pages are elsewhere. */
  const onVolumeChange = (id) => {
    setVolumeId(id);
    const inVolume = days.filter((d) => d.iep_version_id === id);
    if (inVolume.length && !inVolume.some((d) => d.entry_date === activeDate)) goTo(inVolume[0].entry_date);
  };

  /** Leave the diary: back to the child profile this tab belongs to.
   *
   * Derived by dropping the trailing slug rather than navigate(-1), which walks OUT of
   * the app when someone arrives on the diary by a direct link or a refresh.
   */
  const close = useCallback(() => {
    if (onClose) {
      onClose();
      return;
    }
    const path = location.pathname.replace(/\/communication-diary\/?$/, '');
    navigate(path || '/', { replace: true });
  }, [onClose, location.pathname, navigate]);

  // Full screen means the document behind must not scroll too — otherwise the wheel
  // moves the dashboard underneath the paper, which is the second scrollbar in the
  // screenshot. Restored on unmount so leaving the tab cannot strand the app unscrollable.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Escape closes it, as it would any full-screen surface — but the calendar catches the
  // key first (a dropped picker closes before the diary does), and a half-typed note holds
  // it, or the key would silently discard what someone just wrote.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (calOpen) {
        setCalOpen(false);
        return;
      }
      if (!editing) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [close, editing, calOpen]);

  // A click anywhere off the dropped calendar puts it away. `calRef` wraps the button and
  // the sheet together, so pressing the button toggles rather than closes-then-reopens.
  useEffect(() => {
    if (!calOpen) return undefined;
    const onDown = (e) => {
      if (calRef.current && !calRef.current.contains(e.target)) setCalOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [calOpen]);

  const withSave = async (fn, successId) => {
    setSaving(true);
    try {
      const data = await fn();
      if (data) {
        setDays(data.days || []);
        setVolumes(data.volumes || []);
        setCan(data.can || {});
        setChild(data.child || null);
      }
      setToast({ severity: 'success', msg: t(successId) });
      setEditing(false);
    } catch (err) {
      setToast({ severity: 'error', msg: err?.response?.data?.detail || err?.message || t('childDetail.diary.errSave') });
    } finally {
      setSaving(false);
    }
  };

  const onSaveNote = (asQuestion) =>
    withSave(
      () => saveDiaryNote(childId, { entry_date: activeDate, body: draftNote.trim(), is_question: Boolean(asQuestion) }),
      'childDetail.diary.savedNote'
    );

  /* Record which targets the session covered, and drop one off again.
   *
   * `worked_on` is the WHOLE list on the page, snapshotted, so both writes send it in
   * full — the picked targets appended (id/title/domain snapshotted from the live target,
   * matching how the server snapshots a home-activity target), or the chosen row cut out
   * by position. saveDiaryDay upserts the page, so the first target added on a blank day
   * writes it into being. */
  const onAddWorkedOn = () =>
    withSave(async () => {
      const chosen = assignable.filter((tg) => pickWorked.includes(tg.target_id));
      const existing = page?.worked_on || [];
      const additions = chosen.map((tg) => ({ target_id: tg.target_id, title: tg.title, domain: tg.domain }));
      const res = await saveDiaryDay(childId, { entry_date: activeDate, worked_on: [...existing, ...additions] });
      setPickWorked([]);
      return res;
    }, 'childDetail.diary.savedWorkedOn');

  const onRemoveWorkedOn = (index) =>
    withSave(
      () =>
        saveDiaryDay(childId, {
          entry_date: activeDate,
          worked_on: (page?.worked_on || []).filter((_, i) => i !== index)
        }),
      'childDetail.diary.removedWorkedOn'
    );

  const onAddActivity = () =>
    withSave(async () => {
      const res = await addHomeActivity(childId, { entry_date: activeDate, activity_text: draftActivity.trim() });
      setDraftActivity('');
      return res;
    }, 'childDetail.diary.savedActivity');

  const onTick = (id, isDone) => withSave(() => tickHomeActivity(childId, id, isDone), 'childDetail.diary.savedTick');
  const onDeleteActivity = (id) => withSave(() => deleteHomeActivity(childId, id).then(load), 'childDetail.diary.removedActivity');

  const onReportConcern = () =>
    withSave(async () => {
      const res = await reportConcern(childId, {
        entry_date: activeDate,
        concern_type: concernForm.concern_type,
        severity: concernForm.severity,
        body: concernForm.body.trim(),
        action_taken: concernForm.action_taken.trim() || null,
        response_needed: concernForm.response_needed
      });
      setConcernOpen(false);
      setConcernForm(CONCERN_BLANK);
      return res;
    }, 'childDetail.diary.concernReported');

  // One reply flow for both a concern and a caregiver's question: `replyTo` carries which
  // ({ kind, id }). A note reply is a THREAD entry, never an edit of anyone's note.
  const onReply = () =>
    withSave(
      async () => {
        const body = { body: replyDraft.trim() };
        const res =
          replyTo.kind === 'note' ? await replyToNote(childId, replyTo.id, body) : await replyToConcern(childId, replyTo.id, body);
        setReplyTo(null);
        setReplyDraft('');
        return res;
      },
      replyTo?.kind === 'note' ? 'childDetail.diary.questionReplied' : 'childDetail.diary.concernReplied'
    );

  const onCloseConcern = (id) => withSave(() => closeConcern(childId, id), 'childDetail.diary.concernClosed');

  // "Mark closed" on a caregiver's question — the same resolution a concern gets, cleared
  // by the other side of the diary.
  const onResolveNote = (id) => withSave(() => resolveNote(childId, id), 'childDetail.diary.questionResolved');

  // Loading and error render INSIDE the full-screen shell rather than returning early.
  // Returning early left them in the tab's normal flow while the body-scroll lock above
  // was already applied — so a failed load produced a page that could not be scrolled
  // and had no visible way out.

  // ---- the diary's own stylesheet, scoped to this subtree -------------------
  //
  // A FULL-VIEWPORT LAYER, not a panel inside the tab. Sitting in the tab's card gave
  // the diary three scrollbars at once — the document's, the tab body's and the page's —
  // and a leaf barely 300px tall. Negative margins to break out of the card's padding
  // then pushed the whole dashboard wider than the window, which is where the horizontal
  // scrollbar came from. A fixed layer has neither problem: one scroll region, and the
  // paper gets the whole desk.
  const sx = useMemo(
    () => ({
      position: 'fixed',
      inset: 0,
      // Above the sidebar drawer (1200) as a page; above MUI's modals (1300) as an overlay,
      // since the point of the overlay is to cover the dialog that opened it.
      zIndex: overlay ? 1350 : 1250,
      bgcolor: PAPER.desk,
      display: 'flex',
      flexDirection: 'column',
      px: { xs: 1, sm: 2 },
      // More air above than below: the toolbar sits where the app header used to be, and
      // butting it against the top of the window reads as a page that has been cut off.
      pt: { xs: 2, sm: 3 },
      pb: { xs: 1, sm: 2 },
      fontFamily: 'ui-sans-serif,-apple-system,"Segoe UI",Roboto,sans-serif',

      // The masthead: who the diary is about, in the platform's navy. It breaks out of the
      // desk's top and side padding to run full width and flush to the top of the layer,
      // the way an app header sits above its page.
      '& .mast': {
        flex: '0 0 auto',
        bgcolor: PAPER.ink,
        color: '#fff',
        mt: { xs: -2, sm: -3 },
        mx: { xs: -1, sm: -2 },
        mb: { xs: 1.5, sm: 2 },
        px: { xs: 2, sm: 3 },
        py: 1.25
      },
      '& .mast-in': { maxWidth: 1040, width: '100%', mx: 'auto', minWidth: 0 },
      '& .mast .nm': {
        fontFamily: PAPER.serif,
        fontSize: 18,
        fontWeight: 600,
        lineHeight: 1.2,
        letterSpacing: '-.2px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      },
      '& .mast .sub': {
        fontSize: 11.5,
        opacity: 0.72,
        mt: '2px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
      },

      '& .bar': {
        flex: '0 0 auto',
        maxWidth: 1040,
        width: '100%',
        mx: 'auto',
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        flexWrap: 'wrap',
        pb: 1.25
      },

      // The book takes whatever height is left after the toolbar and the flip bar.
      // `perspective` is what makes the turn read as paper lifting rather than as a page
      // squashing sideways: without it rotateY is drawn flat.
      '& .book': {
        flex: '1 1 auto',
        minHeight: 0,
        maxWidth: 1040,
        width: '100%',
        mx: 'auto',
        position: 'relative',
        perspective: '2000px'
      },
      '& .stage': { position: 'relative', height: '100%' },
      // The two leaves peeking out below give the block of paper its thickness.
      '& .book::before, & .book::after': {
        content: '""',
        position: 'absolute',
        left: 20,
        right: 6,
        height: 18,
        bgcolor: PAPER.paper2,
        border: `1px solid ${PAPER.edge}`,
        borderRadius: '2px',
        zIndex: 0
      },
      '& .book::before': { bottom: -5 },
      '& .book::after': { bottom: -9, opacity: 0.55 },

      '& .page': {
        position: 'relative',
        zIndex: 1,
        bgcolor: PAPER.paper,
        border: `1px solid ${PAPER.edge}`,
        borderRadius: '2px 7px 7px 2px',
        pl: '36px',
        overflow: 'hidden',
        boxShadow: '0 1px 1px rgba(20,28,40,.06), 0 8px 22px rgba(20,28,40,.07)',
        display: 'flex',
        flexDirection: 'column',
        // Fills the space the flex parent gives it, rather than a viewport calculation
        // that has to be kept in step with the height of the chrome around it.
        height: '100%'
      },
      // Applied to the leaf ON THE STAGE only, and left in place for the life of that
      // element — re-adding the class after a turn would settle a page that has just been
      // turned, which lands as a second, smaller flip.
      '& .page.settle': { animation: 'settle .26s cubic-bezier(.22,.68,.3,1) both' },
      // The margin rule, and the shadow the binding throws onto the paper.
      '& .page::after': {
        content: '""',
        position: 'absolute',
        left: 50,
        top: 0,
        bottom: 0,
        width: '1px',
        bgcolor: PAPER.rule,
        opacity: 0.5
      },
      '@keyframes settle': {
        from: { transform: 'rotateY(-10deg) translateX(7px)', opacity: 0.35 },
        to: { transform: 'none', opacity: 1 }
      },

      /* ---- turning a page -------------------------------------------------------
       *
       * The paper turns about the binding, so everything past vertical belongs to the far
       * half of the book and must simply be gone. `.clip` is what does that: the leaf keeps
       * rotating inside a box that does not overflow, so it disappears at the hinge instead
       * of sweeping across the desk. Lifted straight from the mockup, including the timings
       * — a turn people recognise is a turn they have seen before. */
      '& .clip': {
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        zIndex: 6,
        pointerEvents: 'none',
        perspective: '2000px',
        perspectiveOrigin: '0% 50%'
      },
      /* Promoted to its own compositing layer BEFORE it moves, so the browser rasterises a
       * page of serif text once and then only recomposites the rotation.
       *
       * Deliberately NO `contain` here, tempting as it looks: paint and layout containment
       * are grouping properties, and a grouping property forces `transform-style` to flat.
       * That would drop the leaf out of the 3D context its parent's perspective sets up, and
       * with it the backface hiding that makes the far half of the turn disappear. `.clip`
       * already clips to the page, so containment buys nothing here anyway. */
      '& .leaf': {
        position: 'absolute',
        inset: 0,
        transformOrigin: 'left center',
        transformStyle: 'preserve-3d',
        willChange: 'transform'
      },
      /* A leaf in flight is scenery for 600ms — it must not become a scroll container. Its
       * own scrollbar used to flash in and out at each turn, and a fresh overflow container
       * is layout work paid on the very frame the animation needs. */
      '& .clip .p-body': { overflowY: 'hidden' },
      // Square at the hinge and rounded at the fore-edge, and hiding its own back: a leaf
      // past 90 degrees is showing you its reverse, which carries no writing.
      '& .leaf .page': {
        borderRadius: '0 7px 7px 0',
        backfaceVisibility: 'hidden',
        boxShadow: '0 2px 5px rgba(20,28,40,.12), 26px 14px 46px rgba(20,28,40,.20)'
      },
      // Light rakes off the sheet as it lifts.
      '& .leaf .curl': {
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        backfaceVisibility: 'hidden',
        borderRadius: '0 7px 7px 0',
        background: 'linear-gradient(to right,rgba(255,255,255,.5),rgba(255,255,255,0) 22%,rgba(20,28,40,0) 62%,rgba(20,28,40,.10))'
      },
      '& .leaf.fwd': { animation: `turnFwd ${TURN_MS}ms ${TURN_EASE} forwards` },
      '& .leaf.back': { animation: `turnBack ${TURN_MS}ms ${TURN_EASE} forwards` },
      '@keyframes turnFwd': { from: { transform: 'rotateY(0deg)' }, to: { transform: 'rotateY(-180deg)' } },
      '@keyframes turnBack': { from: { transform: 'rotateY(-180deg)' }, to: { transform: 'rotateY(0deg)' } },

      // The shadow the lifted leaf throws across the page underneath.
      '& .shade': {
        position: 'absolute',
        inset: 0,
        zIndex: 5,
        pointerEvents: 'none',
        borderRadius: '0 7px 7px 0',
        background: 'linear-gradient(to right, rgba(20,28,40,.26), rgba(20,28,40,.07) 38%, rgba(20,28,40,0) 66%)'
      },
      '& .shade.fwd': { animation: `shadeOut ${TURN_MS}ms ${TURN_EASE} forwards` },
      '& .shade.back': { animation: `shadeIn ${TURN_MS}ms ${TURN_EASE} forwards` },
      '@keyframes shadeOut': { '0%': { opacity: 1 }, '48%': { opacity: 0.55 }, '100%': { opacity: 0 } },
      '@keyframes shadeIn': { '0%': { opacity: 0 }, '52%': { opacity: 0.55 }, '100%': { opacity: 0 } },

      // Asked for less motion, the paper stops moving. goTo() also skips the turn entirely
      // so nothing is left mid-flight; this covers the settle and the shading.
      '@media (prefers-reduced-motion: reduce)': {
        '& .page, & .leaf, & .shade': { animation: 'none !important' }
      },

      // Fixed furniture, so it lives OUTSIDE the paper: the rings do not travel with a
      // leaf that turns around them, and they stay on top of it while it does.
      '& .binding': {
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 36,
        zIndex: 9,
        pointerEvents: 'none',
        borderRight: `1px solid ${PAPER.hair}`,
        background: 'linear-gradient(to right,rgba(20,28,40,.05),rgba(20,28,40,0) 72%)'
      },
      '& .ring': {
        position: 'absolute',
        left: 8,
        width: 20,
        height: 7,
        borderRadius: '8px',
        border: `1.5px solid ${PAPER.ringEdge}`,
        borderTopColor: '#C8CDC4',
        borderBottomColor: '#8B9188',
        background: 'linear-gradient(to bottom,#ECEEE9,#C4C9BF)'
      },
      '& .ring::after': {
        content: '""',
        position: 'absolute',
        left: 6,
        top: '3px',
        width: 8,
        height: 5,
        borderRadius: '50%',
        bgcolor: PAPER.desk,
        boxShadow: 'inset 0 1px 1px rgba(0,0,0,.16)'
      },

      '& .p-head': { flex: '0 0 auto', p: '18px 20px 14px', mr: '20px', borderBottom: `1.5px solid ${PAPER.ink}` },
      '& .dnum': { fontFamily: PAPER.serif, fontSize: 33, fontWeight: 600, lineHeight: 0.95, letterSpacing: '-1px', color: '#1D2024' },
      '& .dmon': { fontSize: 10, textTransform: 'uppercase', letterSpacing: '.9px', color: PAPER.grey, mt: '3px' },
      '& .dow': { fontFamily: PAPER.serif, fontSize: 18, fontWeight: 600, lineHeight: 1.2, color: '#1D2024' },
      '& .sess': { fontSize: 12.5, color: PAPER.grey, mt: '3px' },
      // The IEP label riding beside the session time — a touch stronger than the time so
      // it reads as the volume, not part of the clock.
      '& .sess .sess-iep': { color: PAPER.ink, fontWeight: 600 },

      '& .p-body': {
        flex: '1 1 auto',
        overflowY: 'auto',
        overscrollBehavior: 'contain',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { width: 9 },
        '&::-webkit-scrollbar-thumb': { background: '#D3D7CF', borderRadius: 9, border: `3px solid ${PAPER.paper}` }
      },
      '& .sec': { p: '20px 20px 0', mr: '20px' },

      // Section label: small caps with a hairline running off to the right.
      '& .lbl': {
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        mb: '10px',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '1.2px',
        textTransform: 'uppercase',
        color: PAPER.grey
      },
      '& .lbl::after': { content: '""', flex: 1, height: '1px', bgcolor: PAPER.hair },
      '& .lbl .dot': { width: 7, height: 7, borderRadius: '50%', flex: '0 0 7px' },

      // Anything written on the page is serif. This is the rule that matters.
      '& .prose': { fontFamily: PAPER.serif, fontSize: 16, lineHeight: 1.6, m: 0, whiteSpace: 'pre-wrap' },
      '& .by': { display: 'flex', alignItems: 'baseline', gap: '7px', mt: '9px', fontSize: 11.5, color: PAPER.grey },
      '& .by b': { fontWeight: 600, fontSize: 12.5 },
      '& .by .t': { ml: 'auto', color: PAPER.grey2, fontSize: 11 },

      '& .worked': { listStyle: 'none', m: 0, p: 0 },
      '& .worked li': {
        p: '5px 0',
        borderBottom: `1px solid ${PAPER.hair}`,
        display: 'flex',
        gap: '10px',
        alignItems: 'center',
        flexWrap: 'wrap',
        minHeight: 38
      },
      '& .worked li:last-of-type': { borderBottom: 0 },
      '& .worked li::before': { content: '""', flex: '0 0 5px', height: 5, borderRadius: '50%', bgcolor: PAPER.ink, opacity: 0.35 },
      '& .worked .tg': { fontFamily: PAPER.serif, fontSize: 15.5, lineHeight: 1.4, flex: 1, minWidth: 120, color: '#1D2024' },
      '& .worked .dm': { fontSize: 9.5, letterSpacing: '.7px', textTransform: 'uppercase', color: PAPER.grey2 },

      // The voices.
      '& .v-therapist .prose, & .v-therapist .by b': { color: PAPER.ink },
      '& .v-supervisor': { borderLeft: `2px solid ${PAPER.green}`, pl: '14px' },
      '& .v-supervisor .prose': { color: PAPER.green, fontSize: 15.5 },
      '& .v-supervisor .by b': { color: PAPER.green },
      '& .note-home': { bgcolor: PAPER.paper2, border: `1px solid ${PAPER.edge}`, borderRadius: '3px', p: '13px 15px' },
      '& .note-home .prose': { color: PAPER.pencil, fontSize: 15.5 },
      '& .note-home .by b': { color: PAPER.pencil },
      '& .pending': {
        mt: '8px',
        display: 'inline-block',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '.6px',
        textTransform: 'uppercase',
        color: '#A8322B',
        border: '1px solid #EBC9C5',
        bgcolor: '#FDF4F3',
        borderRadius: '3px',
        p: '2px 7px'
      },

      // ---- the red pen: a concern on the page ----
      '& .lbl.is-red': { color: '#A8322B' },
      '& .lbl.is-red::after': { bgcolor: '#EBC9C5' },
      '& .redbox': {
        border: '1px solid #EBC9C5',
        borderLeft: '3px solid #A8322B',
        bgcolor: '#FDF4F3',
        borderRadius: '2px',
        p: '15px 16px'
      },
      '& .redbox .prose': { color: '#A8322B' },
      '& .redbox .by b': { color: '#A8322B' },
      '& .stamps': { display: 'flex', gap: '6px', flexWrap: 'wrap', mb: '11px' },
      '& .stamp-sev': {
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '.9px',
        textTransform: 'uppercase',
        borderRadius: '2px',
        p: '3px 8px'
      },
      '& .sev-critical': { bgcolor: '#A8322B', color: '#fff' },
      '& .sev-urgent': { bgcolor: '#F7DEDB', color: '#A8322B', border: '1px solid #EBC9C5' },
      '& .sev-awareness': { bgcolor: PAPER.slip, color: PAPER.amber, border: `1px solid ${PAPER.slipEdge}` },
      '& .stamp-type': {
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '.5px',
        textTransform: 'uppercase',
        bgcolor: '#fff',
        border: '1px solid #EBC9C5',
        color: '#A8322B',
        borderRadius: '2px',
        p: '3px 8px'
      },
      '& .redbox .action': { fontSize: 13, color: '#5D3733', mt: '11px', pt: '10px', borderTop: '1px dashed #EBC9C5' },
      '& .stamp-need': {
        display: 'inline-flex',
        alignItems: 'center',
        mt: '12px',
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: '1px',
        textTransform: 'uppercase',
        color: '#A8322B',
        border: '2px solid #A8322B',
        borderRadius: '3px',
        p: '4px 9px'
      },
      '& .replies': { mt: '13px', pt: '12px', borderTop: '1px solid #EBC9C5' },
      '& .replies .prose': { color: '#1D2024', fontSize: 15 },
      '& .reply-item + .reply-item': { mt: '11px', pt: '11px', borderTop: '1px dashed #EBC9C5' },
      '& .closed-note': { fontSize: 11.5, color: PAPER.grey, mt: '11px' },
      '& .c-acts': { display: 'flex', gap: '8px', flexWrap: 'wrap', mt: '13px' },
      '& .btn-red': {
        border: '1px solid #A8322B',
        bgcolor: '#A8322B',
        color: '#fff',
        borderRadius: '6px',
        minHeight: 38,
        p: '0 14px',
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer'
      },
      '& .btn-red:disabled': { opacity: 0.5, cursor: 'default' },

      // The activities for home live on a cream slip tucked into the page.
      '& .slip': { m: '20px 20px 0', bgcolor: PAPER.slip, border: `1px solid ${PAPER.slipEdge}`, borderRadius: '2px', p: '15px 16px' },
      '& .slip .lbl': { color: PAPER.amber },
      '& .slip .lbl::after': { bgcolor: PAPER.slipEdge },
      '& .hp': { listStyle: 'none', m: 0, p: 0 },
      '& .hp li': { display: 'flex', gap: '11px', alignItems: 'center', p: '4px 0', minHeight: 38 },
      '& .hp input': { width: 19, height: 19, flex: '0 0 19px', accentColor: PAPER.amber, cursor: 'pointer', m: 0 },
      '& .hp input:disabled': { cursor: 'default', opacity: 0.5 },
      '& .hp label': {
        fontFamily: PAPER.serif,
        fontSize: 15.5,
        lineHeight: 1.5,
        color: '#3D3524',
        flex: 1,
        minWidth: 0,
        cursor: 'pointer'
      },
      '& .hp li.done label': { color: '#948E7C', textDecoration: 'line-through', textDecorationThickness: '1px' },
      // The target code carried by an assigned target, as a small tag beside its label.
      '& .hp .tag': {
        flex: '0 0 auto',
        fontSize: 9.5,
        fontWeight: 700,
        letterSpacing: '.6px',
        textTransform: 'uppercase',
        color: PAPER.ink,
        bgcolor: '#EAF0F8',
        border: '1px solid #D3DEEC',
        borderRadius: '3px',
        p: '1px 6px'
      },
      '& .tally': { fontSize: 11.5, fontWeight: 600, color: PAPER.amber, mt: '10px' },

      // ---- view tabs: day by day / overdue / attention ----
      '& .tabs': { display: 'flex', gap: '5px', flexWrap: 'wrap' },
      '& .tab': {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        border: `1px solid ${PAPER.desk2}`,
        bgcolor: PAPER.paper,
        borderRadius: '7px',
        p: '0 11px',
        minHeight: 36,
        fontSize: 12.5,
        fontWeight: 500,
        color: PAPER.grey,
        cursor: 'pointer'
      },
      '& .tab[aria-pressed="true"]': { bgcolor: PAPER.ink, borderColor: PAPER.ink, color: '#fff' },
      '& .tab svg': { width: 14, height: 14, stroke: 'currentColor', fill: 'none', strokeWidth: 1.7 },
      '& .tab .n': {
        fontSize: 11,
        fontWeight: 700,
        bgcolor: 'rgba(0,0,0,.07)',
        borderRadius: '9px',
        p: '1px 6px',
        minWidth: 17,
        textAlign: 'center'
      },
      '& .tab[aria-pressed="true"] .n': { bgcolor: 'rgba(255,255,255,.22)' },
      '& .tab .n.hot': { bgcolor: '#A8322B', color: '#fff' },

      // ---- the outstanding-work marks (chips), on the leaf and in the lists ----
      '& .marks': { display: 'flex', gap: '6px', flexWrap: 'wrap', mt: '12px' },
      '& .mk': {
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: '.5px',
        textTransform: 'uppercase',
        border: '1px solid currentColor',
        borderRadius: '3px',
        p: '2px 7px'
      },
      '& .mk.amber': { color: PAPER.amber, bgcolor: PAPER.slip },
      '& .mk.calm': { color: PAPER.grey },
      '& .mk.red': { color: '#A8322B', bgcolor: '#FDF4F3' },

      // ---- the overdue / attention lists ----
      '& .list': { flex: '1 1 auto', minHeight: 0, overflowY: 'auto', maxWidth: 1040, width: '100%', mx: 'auto' },
      '& .list h2': {
        fontFamily: PAPER.serif,
        fontSize: 19,
        m: '0 0 3px',
        display: 'flex',
        alignItems: 'center',
        gap: '9px',
        color: '#1D2024'
      },
      '& .list h2 svg': { width: 19, height: 19, stroke: '#A8322B', fill: 'none', strokeWidth: 1.7 },
      '& .list .sub': { fontSize: 12.5, color: PAPER.grey, mb: '14px' },
      '& .row': {
        display: 'flex',
        gap: '13px',
        width: '100%',
        textAlign: 'left',
        bgcolor: PAPER.paper,
        border: `1px solid ${PAPER.edge}`,
        borderRadius: '5px',
        p: '14px 15px',
        mb: '9px',
        alignItems: 'flex-start',
        cursor: 'pointer'
      },
      '& .row:hover': { borderColor: PAPER.ink },
      '& .row.urgent': { borderLeft: '3px solid #A8322B' },
      '& .row .stub': { flex: '0 0 44px', textAlign: 'center', borderRight: `1px solid ${PAPER.hair}`, pr: '12px' },
      '& .row .stub .n': { fontFamily: PAPER.serif, fontSize: 20, fontWeight: 600, lineHeight: 1 },
      '& .row .stub .m': { fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '.6px', color: PAPER.grey2, mt: '2px' },
      '& .row .snip': { fontFamily: PAPER.serif, fontSize: 14.5, lineHeight: 1.45, color: '#33383E', mb: '7px' },
      '& .row .who': {
        display: 'flex',
        alignItems: 'center',
        gap: '7px',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '.5px',
        textTransform: 'uppercase',
        color: PAPER.grey,
        mb: '6px'
      },
      '& .row .who .dot': { width: 7, height: 7, borderRadius: '50%', flex: '0 0 7px' },
      '& .row .late': { fontSize: 11.5, fontWeight: 600, color: '#A8322B' },
      '& .row .marks': { mt: 0 },
      '& .empty': {
        bgcolor: PAPER.paper,
        border: `1px solid ${PAPER.edge}`,
        borderRadius: '5px',
        p: '40px 24px',
        textAlign: 'center',
        color: PAPER.grey,
        fontSize: 14
      },
      '& .empty .big': { fontFamily: PAPER.serif, fontSize: 17, color: '#1D2024', mb: '5px', display: 'block' },

      // Buttons: plain paper chrome, never MUI's raised look.
      '& .pg, & .btn-sm, & .write-btn, & .iconbtn': {
        font: 'inherit',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px'
      },
      '& .pg': {
        border: `1px solid ${PAPER.desk2}`,
        bgcolor: PAPER.paper,
        borderRadius: '7px',
        minHeight: 36,
        p: '0 11px',
        fontSize: 12.5,
        color: PAPER.ink,
        fontWeight: 500
      },
      '& .pg:disabled': { opacity: 0.35, cursor: 'default' },
      // The red pen — a concern is not part of the paper chrome, so it stands out.
      '& .btn-concern': {
        border: '1px solid #A8322B',
        bgcolor: '#A8322B',
        color: '#fff',
        borderRadius: '7px',
        minHeight: 36,
        p: '0 12px',
        fontSize: 12.5,
        fontWeight: 600,
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px'
      },
      '& .btn-sm': {
        border: `1px solid ${PAPER.ink}`,
        bgcolor: PAPER.ink,
        color: '#fff',
        borderRadius: '6px',
        minHeight: 36,
        p: '0 13px',
        fontSize: 12.5,
        fontWeight: 600
      },
      '& .btn-sm.ghost': { bgcolor: '#fff', color: PAPER.ink },
      '& .btn-sm:disabled': { opacity: 0.45, cursor: 'default' },
      '& .write-btn': {
        width: '100%',
        border: `1px dashed ${PAPER.desk2}`,
        bgcolor: 'transparent',
        borderRadius: '6px',
        minHeight: 46,
        p: '0 14px',
        fontSize: 13.5,
        color: PAPER.grey,
        '&:hover': { borderColor: PAPER.ink, color: PAPER.ink }
      },
      '& .iconbtn': {
        border: '1px solid transparent',
        bgcolor: 'transparent',
        borderRadius: '6px',
        width: 32,
        height: 32,
        color: PAPER.grey,
        flex: '0 0 32px',
        fontSize: 15,
        '&:hover': { bgcolor: PAPER.paper2, borderColor: PAPER.desk2, color: '#A8322B' }
      },
      // The writing surface: serif, because it becomes handwriting.
      '& .edit-box': {
        width: '100%',
        border: `1px solid ${PAPER.ink}`,
        borderRadius: '5px',
        p: '11px 12px',
        fontFamily: PAPER.serif,
        fontSize: 16,
        lineHeight: 1.6,
        bgcolor: '#fff',
        minHeight: 110,
        resize: 'vertical',
        color: '#1D2024'
      },
      '& .addrow': { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', mt: '11px' },
      '& .addrow input': {
        flex: 1,
        minWidth: 0,
        border: `1px solid ${PAPER.desk2}`,
        borderRadius: '6px',
        p: '9px 10px',
        fontFamily: PAPER.serif,
        fontSize: 14.5,
        bgcolor: '#fff',
        minHeight: 40,
        color: '#1D2024'
      },

      '& .flipbar': {
        flex: '0 0 auto',
        maxWidth: 1040,
        width: '100%',
        mx: 'auto',
        mt: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      },
      '& .flipbar .pos': { flex: 1, textAlign: 'center', fontSize: 12, color: PAPER.grey },
      '& .flipbar .pos b': { fontFamily: PAPER.serif, fontSize: 14, color: '#1D2024' },

      /* ---- the date picker: a paper button that drops a month calendar ----
       * The button carries the day on show ("20 Jul"); the sheet below it is the diary's
       * own calendar, absolutely placed against the wrapper so it hangs off the toolbar
       * rather than pushing it. Lifted from the mockup, colours and all. */
      '& .calwrap': { position: 'relative', flex: '0 0 auto' },
      '& .calbtn': {
        font: 'inherit',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        border: `1px solid ${PAPER.desk2}`,
        bgcolor: PAPER.paper,
        borderRadius: '7px',
        minHeight: 36,
        p: '0 11px',
        fontSize: 12.5,
        color: '#1D2024'
      },
      '& .calbtn svg': { width: 15, height: 15, stroke: PAPER.ink, fill: 'none', strokeWidth: 1.6 },
      '& .cal': {
        position: 'absolute',
        right: 0,
        top: 'calc(100% + 8px)',
        // Above the leaves (max z 9) and the binding within this fixed layer.
        zIndex: 30,
        width: 288,
        bgcolor: PAPER.paper,
        border: `1px solid ${PAPER.edge}`,
        borderRadius: '10px',
        boxShadow: '0 12px 32px rgba(20,28,40,.2)',
        p: '12px'
      },
      '& .cal-hd': { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '8px' },
      '& .cal-hd .mo': { fontFamily: PAPER.serif, fontSize: 15, fontWeight: 600, color: '#1D2024' },
      '& .cal-nav': {
        cursor: 'pointer',
        border: `1px solid ${PAPER.hair}`,
        bgcolor: '#fff',
        borderRadius: '6px',
        width: 32,
        height: 32,
        fontSize: 15,
        color: PAPER.ink,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center'
      },
      '& .cal-grid': { display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '2px' },
      '& .cal-grid .dw': {
        fontSize: 9.5,
        textTransform: 'uppercase',
        letterSpacing: '.5px',
        color: PAPER.grey2,
        textAlign: 'center',
        p: '3px 0'
      },
      '& .cal-day': {
        aspectRatio: '1',
        border: 0,
        background: 'transparent',
        borderRadius: '6px',
        fontSize: 13,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',
        minHeight: 34,
        color: '#1D2024',
        cursor: 'pointer'
      },
      '& .cal-day:disabled': { color: '#C9CCD0', cursor: 'default' },
      '& .cal-day.has': { fontWeight: 600 },
      '& .cal-day.has:hover': { bgcolor: '#EDF1F6' },
      '& .cal-day.cur': { bgcolor: PAPER.ink, color: '#fff' },
      '& .cal-day .pip': { width: 5, height: 5, borderRadius: '50%', bgcolor: PAPER.ink },
      '& .cal-day .pip.att': { bgcolor: '#A8322B' },
      '& .cal-day.cur .pip': { bgcolor: '#fff' },
      '& .cal-quick': { display: 'flex', gap: '6px', mt: '10px', borderTop: `1px solid ${PAPER.hair}`, pt: '10px' },
      '& .cal-quick button': {
        flex: 1,
        cursor: 'pointer',
        border: `1px solid ${PAPER.hair}`,
        bgcolor: '#fff',
        borderRadius: '6px',
        minHeight: 34,
        fontSize: 12,
        color: PAPER.ink
      },

      // The report-a-concern bar sits at the foot of the page, the red button pushed to
      // the right the way the flip bar keeps its own controls to the edges.
      '& .concernbar': {
        flex: '0 0 auto',
        maxWidth: 1040,
        width: '100%',
        mx: 'auto',
        mt: '10px',
        display: 'flex',
        justifyContent: 'flex-end'
      },
      '& .btn-concern svg': { width: 15, height: 15, fill: 'currentColor', flex: '0 0 15px' }
    }),
    // `overlay` alone: it is the only value in here that can change, and menuZ is derived
    // from it (the Selects read menuZ directly, not through this stylesheet).
    [overlay]
  );

  const fmtTime = (iso) => new Date(iso).toLocaleTimeString(intl.locale, { hour: '2-digit', minute: '2-digit' });

  const voiceBlock = (voice, dayPage) => {
    const note = (dayPage?.notes || []).find((n) => n.author_role_code === voice) || null;
    if (!note) return null;
    const cls = voice === 'caregiver' ? 'note-home' : `v-${voice}`;
    // A question waits on the OTHER side of the diary, exactly like a concern: whoever may
    // answer it gets the same two actions — Respond (open a reply THREAD, never an edit of
    // anyone's note) and Mark closed (resolve the awaiting-reply flag). The author's own
    // side sees neither.
    const canAnswer = Boolean(can.voice) && note.is_question && isHomeVoice(can.voice) !== isHomeVoice(note.author_role_code);
    const replies = note.replies || [];
    return (
      <Box className="sec" key={voice}>
        <Box className="lbl">
          <Box className="dot" sx={{ bgcolor: voice === 'therapist' ? PAPER.ink : voice === 'supervisor' ? PAPER.green : PAPER.pencil }} />
          {t(`childDetail.diary.voice.${voice}`)}
        </Box>
        <Box className={cls}>
          <Box component="p" className="prose">
            {note.body}
          </Box>
          <Box className="by">
            <b>{note.author_name}</b>
            <span className="t">{fmtTime(note.created_at)}</span>
          </Box>
          {note.is_question && <Box className="pending">{t('childDetail.diary.awaitingReply')}</Box>}
          {/* The reply thread — the other side's answers, oldest first, same shape as a
              concern's. Stays visible after the question is marked closed. */}
          {replies.length > 0 && (
            <Box className="replies">
              {replies.map((r, i) => (
                <Box className="reply-item" key={i}>
                  <Box component="p" className="prose">
                    {r.body}
                  </Box>
                  <Box className="by">
                    <b>{r.author_name}</b>
                    <span>{t(`childDetail.diary.voice.${r.author_role_code}`)}</span>
                    <span className="t">{fmtTime(r.created_at)}</span>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {canAnswer && (
            <Box className="c-acts">
              {/* Respond opens a reply dialog whose text is appended to this question's
                  thread — it does NOT touch the responder's own note. */}
              <button
                type="button"
                className="btn-red"
                onClick={() => {
                  setReplyTo({ kind: 'note', id: note.id });
                  setReplyDraft('');
                }}
              >
                {t('childDetail.diary.respond')}
              </button>
              <button type="button" className="btn-sm ghost" onClick={() => onResolveNote(note.id)} disabled={saving}>
                {t('childDetail.diary.markClosed')}
              </button>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const fmtDateTime = (iso) =>
    new Date(iso).toLocaleString(intl.locale, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  // The red pen. A concern written on the page, with its thread and — for whoever may —
  // the way to answer it or, for a clinician, to close it.
  const concernBlock = (dayPage, concern) => {
    const closed = concern.status === 'closed';
    const canReply = Boolean(can.voice) && !closed && isHomeVoice(can.voice) !== isHomeVoice(concern.author_role_code);
    const canClose = Boolean(can.close_concern) && !closed;
    // A concern is answered by the OTHER side, so the stamp names who owes the reply: a
    // caregiver's concern waits on the care team, a clinician's on the family.
    const responseNeededKey = isHomeVoice(concern.author_role_code)
      ? 'childDetail.diary.responseNeededFromCareTeam'
      : 'childDetail.diary.responseNeededFromFamily';
    return (
      <Box className="sec" key={concern.id}>
        <Box className="lbl is-red">
          <Box className="dot" sx={{ bgcolor: '#A8322B' }} />
          {t('childDetail.diary.concernRaised')}
        </Box>
        <Box className="redbox">
          <Box className="stamps">
            <Box component="span" className={`stamp-sev sev-${concern.severity}`}>
              {t(`childDetail.diary.severity.${concern.severity}`)}
            </Box>
            <Box component="span" className="stamp-type">
              {t(`childDetail.diary.concernType.${concern.concern_type}`)}
            </Box>
          </Box>
          <Box component="p" className="prose">
            {concern.body}
          </Box>
          {concern.action_taken && (
            <Box className="action">
              <b>{t('childDetail.diary.actionTaken')}</b> {concern.action_taken}
            </Box>
          )}
          <Box className="by">
            <b style={{ color: '#A8322B' }}>{concern.author_name}</b>
            <span>{t(`childDetail.diary.voice.${concern.author_role_code}`)}</span>
            <span className="t">{fmtTime(concern.created_at)}</span>
          </Box>
          {concern.response_needed && !closed && <Box className="stamp-need">{t(responseNeededKey)}</Box>}
          {concern.replies?.length > 0 && (
            <Box className="replies">
              {concern.replies.map((r, i) => (
                <Box className="reply-item" key={i}>
                  <Box component="p" className="prose">
                    {r.body}
                  </Box>
                  <Box className="by">
                    <b>{r.author_name}</b>
                    <span>{t(`childDetail.diary.voice.${r.author_role_code}`)}</span>
                    <span className="t">{fmtTime(r.created_at)}</span>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {closed && (
            <Box className="closed-note">
              {t('childDetail.diary.concernClosedBy', {
                name: concern.closed_by_name || '',
                at: concern.closed_at ? fmtDateTime(concern.closed_at) : ''
              })}
            </Box>
          )}
          {(canReply || canClose) && (
            <Box className="c-acts">
              {canReply && (
                <button
                  type="button"
                  className="btn-red"
                  onClick={() => {
                    setReplyTo({ kind: 'concern', id: concern.id });
                    setReplyDraft('');
                  }}
                >
                  {t('childDetail.diary.respond')}
                </button>
              )}
              {canClose && (
                <button type="button" className="btn-sm ghost" onClick={() => onCloseConcern(concern.id)} disabled={saving}>
                  {t('childDetail.diary.markClosed')}
                </button>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  /* ONE LEAF OF PAPER, drawn for whichever day it belongs to.
   *
   * A function of the DATE rather than of component state, because a page turn has two
   * leaves on screen at once — the one you are leaving and the one you are arriving at —
   * and they are the same markup at two different dates.
   *
   * `live` is false for the leaf in flight: it never opens the editor, and the layer
   * carrying it takes no pointer events, so its buttons are scenery. `cls` is how the
   * leaf resting on the stage gets its settle animation and the flying one does not.
   */
  const leaf = (iso, live, cls) => {
    const page = dayFor(iso);
    const dt = new Date(`${iso}T00:00:00`);
    const activities = page?.home_activities || [];
    // The "for home" slip carries the activities typed for the caregiver to do at home.
    const homeActivities = activities.filter((a) => a.source !== 'target');
    const homeDone = homeActivities.filter((a) => a.is_done).length;
    const workedOn = page?.worked_on || [];
    // Targets already recorded as worked on drop out of the picker, so the same one cannot
    // be added twice.
    const workedIds = new Set(workedOn.map((w) => w.target_id).filter(Boolean));
    const pickableWorked = assignable.filter((tg) => !workedIds.has(tg.target_id));
    const writing = live && editing;

    return (
      <Box className={`page${cls}`}>
        {/* dateline: fixed to the leaf, everything written scrolls under it */}
        <Box className="p-head">
          <Stack direction="row" spacing={1.75} alignItems="flex-start">
            <Box sx={{ textAlign: 'center', flex: '0 0 auto', minWidth: 50 }}>
              <Box className="dnum">{dt.getDate()}</Box>
              <Box className="dmon">{dt.toLocaleDateString(intl.locale, { month: 'short' })}</Box>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box className="dow">{dt.toLocaleDateString(intl.locale, { weekday: 'long' })}</Box>
              <Box className="sess">
                {page?.session_slot || (page ? t('childDetail.diary.noSession') : t('childDetail.diary.blankPage'))}
                {/* The IEP the page belongs to, beside the time — a page carries its own
                    volume, so a reader turning through days can see when it changes. */}
                {page?.iep_version_id && volumeById.get(page.iep_version_id)?.short_label && (
                  <Box component="span" className="sess-iep">
                    {' · '}
                    {volumeById.get(page.iep_version_id).short_label}
                  </Box>
                )}
              </Box>
            </Box>
            <Box sx={{ flex: 1 }} />
            {/* One day at a time, blank days included, the way you would turn to a
                date in a paper diary; the flip bar at the foot of the screen does the
                same thing with words on it. Nothing forward of today, and no floor
                going back.

                A disabled button fires no events, so the Tooltip has to listen on a
                wrapper rather than on the button itself — hence the spans. */}
            <Stack direction="row" spacing={0.75}>
              <Tooltip title={t('childDetail.diary.prevDay')}>
                <span>
                  <button
                    type="button"
                    className="pg"
                    aria-label={t('childDetail.diary.prevDay')}
                    onClick={() => goTo(shiftDay(activeDate, -1))}
                  >
                    ‹
                  </button>
                </span>
              </Tooltip>
              <Tooltip title={t('childDetail.diary.nextDay')}>
                <span>
                  <button
                    type="button"
                    className="pg"
                    aria-label={t('childDetail.diary.nextDay')}
                    disabled={activeDate >= lastDate}
                    onClick={() => goTo(shiftDay(activeDate, 1))}
                  >
                    ›
                  </button>
                </span>
              </Tooltip>
            </Stack>
          </Stack>
          {/* What is still outstanding on this page, in the same words the summary lists use. */}
          {marksFor(page).length > 0 && (
            <Box className="marks">
              {marksFor(page).map((m, i) => (
                <Box key={i} component="span" className={`mk ${m.tone}`}>
                  {m.label}
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box className="p-body">
          {/* ---- concerns, in red, above everything else on the page ---- */}
          {(page?.concerns || []).map((c) => concernBlock(page, c))}

          {/* ---- worked on: the targets the session covered ---- */}
          {/* Everyone reads what was worked on; only the therapist gets the empty-section
              affordance and the controls below (can.set_worked_on). */}
          {(workedOn.length > 0 || can.set_worked_on) && (
            <Box className="sec">
              <Box className="lbl">{t('childDetail.diary.workedOn')}</Box>
              {workedOn.length > 0 ? (
                <Box component="ul" className="worked">
                  {workedOn.map((w, i) => (
                    <li key={`${w.title}-${i}`}>
                      <span className="tg">{w.title}</span>
                      {w.domain && <span className="dm">{w.domain}</span>}
                      {/* The therapist can strike a target off the page again; the supervisor
                          and caregiver only read it, so the remove appears for the therapist
                          alone. */}
                      {can.set_worked_on && (
                        <Tooltip title={t('childDetail.diary.removeActivity')}>
                          <span>
                            <button
                              type="button"
                              className="iconbtn"
                              aria-label={t('childDetail.diary.removeActivity')}
                              disabled={saving}
                              onClick={() => onRemoveWorkedOn(i)}
                            >
                              ×
                            </button>
                          </span>
                        </Tooltip>
                      )}
                    </li>
                  ))}
                </Box>
              ) : (
                <Box component="p" className="prose" sx={{ color: PAPER.grey2, fontSize: 15 }}>
                  {t('childDetail.diary.nothingWorkedOn')}
                </Box>
              )}
              {can.set_worked_on && (
                <>
                  {/* Record which of the child's targets the session covered. The list is
                      the active IEP, and a target already down for the day is filtered out. */}
                  {pickableWorked.length === 0 ? (
                    assignable.length > 0 && (
                      <Box component="p" className="prose" sx={{ color: PAPER.grey2, fontSize: 13, mt: '10px' }}>
                        {t('childDetail.diary.allTargetsWorkedOn')}
                      </Box>
                    )
                  ) : (
                    <Box className="addrow">
                      <TextField
                        select
                        size="small"
                        value={pickWorked}
                        onChange={(e) => setPickWorked(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                        SelectProps={{
                          multiple: true,
                          displayEmpty: true,
                          MenuProps: { sx: { zIndex: menuZ } },
                          renderValue: (sel) =>
                            sel.length === 0
                              ? t('childDetail.diary.chooseTargets')
                              : t('childDetail.diary.targetsChosen', { count: sel.length })
                        }}
                        sx={{ flex: 1, minWidth: 200, bgcolor: '#fff', '& .MuiInputBase-root': { fontSize: 13.5 } }}
                      >
                        {pickableWorked.map((tg) => (
                          <MenuItem key={tg.target_id} value={tg.target_id} sx={{ fontSize: 13.5, whiteSpace: 'normal' }}>
                            {tg.target_code ? `${tg.target_code} — ${tg.title}` : tg.title}
                            {tg.domain ? ` (${tg.domain})` : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                      <button type="button" className="btn-sm ghost" onClick={onAddWorkedOn} disabled={saving || pickWorked.length === 0}>
                        {t('childDetail.diary.addWorkedOn')}
                      </button>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}

          {/* ---- the three voices, in the order a page reads ---- */}
          {VOICES.map((v) => voiceBlock(v, page))}

          {/* ---- your own hand ---- */}
          {can.note && (
            <Box className="sec">
              {!writing ? (
                <button type="button" className="write-btn" onClick={() => setEditing(true)}>
                  ✎&nbsp;
                  {myNote ? t('childDetail.diary.editYourNote') : t(`childDetail.diary.writeAs.${can.voice}`)}
                </button>
              ) : (
                <>
                  <Box className="lbl">
                    <Box
                      className="dot"
                      sx={{ bgcolor: can.voice === 'therapist' ? PAPER.ink : can.voice === 'supervisor' ? PAPER.green : PAPER.pencil }}
                    />
                    {myNote ? t('childDetail.diary.editYourNote') : t(`childDetail.diary.writeAs.${can.voice}`)}
                  </Box>
                  <Box
                    component="textarea"
                    className="edit-box"
                    rows={4}
                    value={draftNote}
                    placeholder={t(`childDetail.diary.placeholder.${can.voice}`)}
                    onChange={(e) => setDraftNote(e.target.value)}
                  />
                  <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} alignItems="center">
                    <button type="button" className="btn-sm" onClick={() => onSaveNote(false)} disabled={saving || !draftNote.trim()}>
                      {myNote ? t('childDetail.diary.updateNote') : t('childDetail.diary.addNote')}
                    </button>
                    {/* Only the caregiver marks a note a question — that is what waits on the
                        team and shows under Attention until a clinician writes back. */}
                    {can.voice === 'caregiver' && (
                      <button
                        type="button"
                        className="btn-sm ghost"
                        onClick={() => onSaveNote(true)}
                        disabled={saving || !draftNote.trim()}
                      >
                        {t('childDetail.diary.askQuestion')}
                      </button>
                    )}
                    <button
                      type="button"
                      className="pg"
                      onClick={() => {
                        setEditing(false);
                        setDraftNote(myNote?.body || '');
                      }}
                    >
                      {t('childDetail.diary.cancel')}
                    </button>
                  </Stack>
                </>
              )}
            </Box>
          )}

          {!can.note && (
            <Box className="sec">
              <Box component="p" className="prose" sx={{ fontSize: 14, color: PAPER.grey }}>
                {t('childDetail.diary.readOnly')}
              </Box>
            </Box>
          )}

          {/* ---- the slip for home ---- */}
          <Box className="slip">
            <Box className="lbl">{t('childDetail.diary.forHome')}</Box>
            {homeActivities.length === 0 ? (
              <Box component="p" className="prose" sx={{ color: '#948E7C', fontSize: 15 }}>
                {t('childDetail.diary.noActivities')}
              </Box>
            ) : (
              <Box component="ul" className="hp">
                {homeActivities.map((a) => (
                  <li key={a.id} className={a.is_done ? 'done' : ''}>
                    <input
                      type="checkbox"
                      id={`hp-${a.id}`}
                      checked={a.is_done}
                      disabled={!can.tick_home_activities || saving}
                      onChange={(e) => onTick(a.id, e.target.checked)}
                    />
                    <label htmlFor={`hp-${a.id}`}>{a.activity_text}</label>
                    {can.set_home_activities && (
                      <Tooltip title={t('childDetail.diary.removeActivity')}>
                        <span>
                          <button
                            type="button"
                            className="iconbtn"
                            aria-label={t('childDetail.diary.removeActivity')}
                            disabled={saving}
                            onClick={() => onDeleteActivity(a.id)}
                          >
                            ×
                          </button>
                        </span>
                      </Tooltip>
                    )}
                  </li>
                ))}
              </Box>
            )}
            {homeActivities.length > 0 && (
              <Box className="tally">{t('childDetail.diary.doneCount', { done: homeDone, total: homeActivities.length })}</Box>
            )}
            {can.set_home_activities && (
              <Box className="addrow">
                <input
                  type="text"
                  value={draftActivity}
                  placeholder={t('childDetail.diary.addActivityPlaceholder')}
                  onChange={(e) => setDraftActivity(e.target.value)}
                />
                <button type="button" className="btn-sm ghost" onClick={onAddActivity} disabled={saving || !draftActivity.trim()}>
                  {t('childDetail.diary.addActivity')}
                </button>
              </Box>
            )}
          </Box>

          <Box sx={{ height: 16 }} />
        </Box>
      </Box>
    );
  };

  /* ---- the two summary lists: overdue, and everything wanting attention ----
   * Both read the SAME pages the book turns through, filtered and shown newest-first, with
   * the therapist's note (or the caregiver's) as a one-line snippet. A row is a way back into
   * the book at that page. */
  // The date tab down the left of every list row, and the way back into the book.
  const rowStub = (iso) => {
    const dt = new Date(`${iso}T00:00:00`);
    return (
      <Box component="span" className="stub">
        <Box component="span" className="n" sx={{ display: 'block' }}>
          {dt.getDate()}
        </Box>
        <Box component="span" className="m" sx={{ display: 'block' }}>
          {dt.toLocaleDateString(intl.locale, { month: 'short' })}
        </Box>
      </Box>
    );
  };
  const snip = (text) => (text.length > 140 ? `${text.slice(0, 140)}…` : text);
  const voiceColour = (voice) => (voice === 'therapist' ? PAPER.ink : voice === 'supervisor' ? PAPER.green : PAPER.pencil);

  // A note still waiting on a reply: who wrote it, what they wrote, and an "awaiting" mark.
  const noteRow = (day, note) => (
    <Box
      component="button"
      type="button"
      className="row"
      key={`note-${day.entry_date}-${note.author_role_code}`}
      onClick={() => goTo(day.entry_date)}
    >
      {rowStub(day.entry_date)}
      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
        <Box component="span" className="who">
          <Box component="span" className="dot" sx={{ bgcolor: voiceColour(note.author_role_code) }} />
          {note.author_name} · {t(`childDetail.diary.voice.${note.author_role_code}`)}
        </Box>
        <Box component="span" className="snip" sx={{ display: 'block' }}>
          {snip(note.body || '')}
        </Box>
        <Box component="span" className="marks" sx={{ display: 'flex' }}>
          <Box component="span" className="mk amber">
            {t('childDetail.diary.awaitingReply')}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // An overdue home activity: the chore, and how long its box has been sitting unticked. In
  // the Attention list it wears an "overdue" mark so it reads apart from the notes.
  const overdueRow = (day, activity, marked) => (
    <Box component="button" type="button" className="row" key={`act-${activity.id}`} onClick={() => goTo(day.entry_date)}>
      {rowStub(day.entry_date)}
      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
        <Box component="span" className="snip" sx={{ display: 'block' }}>
          {activity.activity_text}
        </Box>
        {marked && (
          <Box component="span" className="marks" sx={{ display: 'flex' }}>
            <Box component="span" className="mk amber">
              {t('childDetail.diary.markOverdue')}
            </Box>
          </Box>
        )}
        <Box component="span" className="late">
          {t('childDetail.diary.setDaysAgo', { days: daysAgo(day.entry_date) })}
        </Box>
      </Box>
    </Box>
  );

  // An open concern: its kind and severity, the gist, and a red flag. The border marks it
  // out from the notes and chores it sits among in the Attention list.
  const concernRow = (day, concern) => (
    <Box component="button" type="button" className="row urgent" key={`concern-${concern.id}`} onClick={() => goTo(day.entry_date)}>
      {rowStub(day.entry_date)}
      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
        <Box component="span" className="who">
          <Box component="span" className="dot" sx={{ bgcolor: '#A8322B' }} />
          {t(`childDetail.diary.concernType.${concern.concern_type}`)} · {t(`childDetail.diary.severity.${concern.severity}`)}
        </Box>
        <Box component="span" className="snip" sx={{ display: 'block' }}>
          {snip(concern.body || '')}
        </Box>
        <Box component="span" className="marks" sx={{ display: 'flex' }}>
          <Box component="span" className="mk red">
            {concern.response_needed ? t('childDetail.diary.responseNeeded') : t('childDetail.diary.concernRaised')}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  const listView = () => {
    const attention = view === 'attention';
    const items = attention ? attentionItems : overdueItems;
    const volLabel = volumeById.get(volumeId)?.label || '';
    return (
      <Box className="list">
        <Box component="h2">
          {attention && BELL_ICON}
          {attention ? t('childDetail.diary.attentionHeading') : t('childDetail.diary.overdueHeading')}
        </Box>
        <Box className="sub">
          {volLabel} ·{' '}
          {attention
            ? t('childDetail.diary.attentionCount', { count: items.length })
            : t('childDetail.diary.overdueCount', { count: items.length })}
        </Box>
        {items.length === 0 ? (
          <Box className="empty">
            <Box component="span" className="big">
              {t('childDetail.diary.allClear')}
            </Box>
            {attention ? t('childDetail.diary.attentionEmpty') : t('childDetail.diary.overdueEmpty')}
          </Box>
        ) : attention ? (
          // Open concerns, notes waiting on a reply, and overdue chores — newest day first.
          attentionItems.map((it) =>
            it.kind === 'concern'
              ? concernRow(it.day, it.concern)
              : it.kind === 'note'
                ? noteRow(it.day, it.note)
                : overdueRow(it.day, it.activity, true)
          )
        ) : (
          // Just the overdue chores.
          overdueItems.map(({ day, activity }) => overdueRow(day, activity, false))
        )}
      </Box>
    );
  };

  return (
    /* Rendered into document.body. `position: fixed` is only as good as its containing
       block, and inside the dashboard's main column the layer came up UNDER the app
       header — the strip of chrome left showing across the top of the diary. At the top
       of the document there is nothing left to sit under. */
    <Portal>
      <Box sx={sx}>
        {/* ---------------- masthead: who the diary is about ---------------- */}
        {child && (child.name || mastSubtitle) && (
          <Box className="mast">
            <Box className="mast-in">
              <Box className="nm">{child.name || t('childDetail.tab.communicationDiary')}</Box>
              {mastSubtitle && <Box className="sub">{mastSubtitle}</Box>}
            </Box>
          </Box>
        )}

        {/* ---------------- toolbar: the volume, and which day ---------------- */}
        <Box className="bar">
          <Box
            sx={{
              fontFamily: PAPER.serif,
              fontSize: 17,
              fontWeight: 600,
              color: PAPER.ink,
              mr: 1,
              display: { xs: 'none', sm: 'block' }
            }}
          >
            {t('childDetail.tab.communicationDiary')}
          </Box>
          <TextField
            select
            size="small"
            value={volumeId || ''}
            onChange={(e) => onVolumeChange(e.target.value)}
            SelectProps={{ MenuProps: { sx: { zIndex: menuZ } } }}
            sx={{ minWidth: 180, '& .MuiInputBase-root': { bgcolor: PAPER.paper, fontSize: 12.5 } }}
          >
            {volumes.map((v) => (
              <MenuItem key={v.iep_version_id} value={v.iep_version_id}>
                {v.label}
                {v.is_active ? ` — ${t('childDetail.diary.current')}` : v.is_closed ? ` ${t('childDetail.diary.closed')}` : ''}
              </MenuItem>
            ))}
          </TextField>
          {/* Day by day, or one of the two summary readings. The counts are the tab's point:
              a reader can see there is something overdue or waiting without opening it. */}
          <Box className="tabs" role="group" aria-label={t('childDetail.diary.view')}>
            <button type="button" className="tab" aria-pressed={view === 'day'} onClick={() => setView('day')}>
              {t('childDetail.diary.viewDayByDay')}
            </button>
            <button type="button" className="tab" aria-pressed={view === 'overdue'} onClick={() => setView('overdue')}>
              {t('childDetail.diary.viewOverdue')}
              <Box component="span" className="n">
                {counts.overdue}
              </Box>
            </button>
            <button type="button" className="tab" aria-pressed={view === 'attention'} onClick={() => setView('attention')}>
              {BELL_ICON}
              {t('childDetail.diary.viewAttention')}
              <Box component="span" className={`n${counts.attention ? ' hot' : ''}`}>
                {counts.attention}
              </Box>
            </button>
          </Box>
          <Box sx={{ flex: 1 }} />
          {/* The date picker: a button carrying the day on show, dropping the diary's own
              month calendar. `calRef` wraps both so an off-click closes it but the button
              itself only toggles. */}
          <Box className="calwrap" ref={calRef}>
            <button
              type="button"
              className="calbtn"
              aria-haspopup="dialog"
              aria-expanded={calOpen}
              aria-label={t('childDetail.diary.pickDate')}
              onClick={toggleCal}
            >
              <svg viewBox="0 0 24 24" strokeLinecap="round" aria-hidden="true">
                <rect x="3" y="5" width="18" height="16" rx="2" />
                <path d="M3 10h18M8 3v4M16 3v4" />
              </svg>
              {new Date(`${activeDate}T00:00:00`).toLocaleDateString(intl.locale, { day: 'numeric', month: 'short' })}
            </button>
            {calOpen && (
              <Box className="cal" role="dialog" aria-label={t('childDetail.diary.pickDate')}>
                <Box className="cal-hd">
                  <button type="button" className="cal-nav" aria-label={t('childDetail.diary.prevMonth')} onClick={() => stepMonth(-1)}>
                    ‹
                  </button>
                  <span className="mo">{calMonthLabel}</span>
                  <button type="button" className="cal-nav" aria-label={t('childDetail.diary.nextMonth')} onClick={() => stepMonth(1)}>
                    ›
                  </button>
                </Box>
                <Box className="cal-grid">
                  {weekdayHeads.map((w, i) => (
                    <Box component="span" className="dw" key={`dw-${i}`}>
                      {w}
                    </Box>
                  ))}
                  {calCells.map((cell, i) =>
                    cell === null ? (
                      <span key={`blank-${i}`} />
                    ) : (
                      <button
                        type="button"
                        key={cell.iso}
                        className={`cal-day${cell.day ? ' has' : ''}${cell.iso === activeDate ? ' cur' : ''}`}
                        disabled={!cell.day}
                        onClick={() => pickDate(cell.iso)}
                      >
                        {cell.n}
                        {cell.day && <span className={`pip${dayNeedsAttention(cell.day) ? ' att' : ''}`} />}
                      </button>
                    )
                  )}
                </Box>
                <Box className="cal-quick">
                  <button type="button" onClick={goLatestPage}>
                    {t('childDetail.diary.latestPage')}
                  </button>
                  <button type="button" onClick={goFirstFlagged}>
                    {t('childDetail.diary.firstFlagged')}
                  </button>
                </Box>
              </Box>
            )}
          </Box>
          <button type="button" className="pg" onClick={() => goTo(todayISO())}>
            {t('childDetail.diary.today')}
          </button>
          {/* The way out. A full-screen surface that covers the navigation has to offer
            one, or the only exit is the browser's back button. */}
          <Tooltip title={t('childDetail.diary.closeDiary')}>
            <button type="button" className="pg" onClick={close} aria-label={t('childDetail.diary.closeDiary')}>
              ✕
            </button>
          </Tooltip>
        </Box>

        {saving && <LinearProgress sx={{ maxWidth: 1040, width: '100%', mx: 'auto', mb: 1 }} />}

        {loading && (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
            <CircularProgress />
          </Stack>
        )}

        {!loading && error && (
          <Stack alignItems="center" justifyContent="center" sx={{ flex: 1, px: 2 }}>
            <Alert severity="warning" sx={{ maxWidth: 560 }}>
              {error}
            </Alert>
          </Stack>
        )}

        {/* ---------------- the summary lists ---------------- */}
        {!loading && !error && view !== 'day' && listView()}

        {/* ---------------- the book ---------------- */}
        {!loading && !error && view === 'day' && (
          <Box className="book">
            {/* The binding is furniture, not paper. It lives outside the leaves so a page
              can turn AROUND it, and above them so a leaf in flight passes behind the
              rings the way it would in a real notebook. */}
            <Box className="binding" aria-hidden>
              {RINGS.map((_, i) => (
                <Box key={i} className="ring" sx={{ top: `${3.5 + (i * 93) / (RINGS.length - 1)}%` }} />
              ))}
            </Box>

            <Box className="stage" key={settleKey}>
              {leaf(stageDate, !turning, ' settle')}
            </Box>

            {/* The turn itself: a shadow across the page underneath, and the leaf swinging
              about the hinge inside a box that clips it at the binding. Both come off the
              stage the moment the animation is done. */}
            {turning && (
              <>
                <Box className={`shade ${turning.dir > 0 ? 'fwd' : 'back'}`} aria-hidden />
                <Box className="clip" aria-hidden>
                  <Box className={`leaf ${turning.dir > 0 ? 'fwd' : 'back'}`}>
                    {leaf(flyingDate, false, '')}
                    <Box component="span" className="curl" />
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}

        {/* ---------------- flip bar ---------------- */}
        {!loading && !error && view === 'day' && (
          <Box className="flipbar">
            <button type="button" className="pg" onClick={() => goTo(shiftDay(activeDate, -1))}>
              ‹ {t('childDetail.diary.earlier')}
            </button>
            <Box className="pos">
              {/* A blank day says so rather than claiming a page number. Before day-by-day
                navigation existed the reader could only ever be on a written page, so the
                fallback here counted the last one — which would now read as page N of N
                while looking at an empty leaf. */}
              {!writtenDates.length ? (
                t('childDetail.diary.noPages')
              ) : posInWritten < 0 ? (
                t('childDetail.diary.blankPage')
              ) : (
                <>
                  <b>{writtenDates.length - posInWritten}</b> {t('childDetail.diary.ofPages', { total: writtenDates.length })}
                </>
              )}
            </Box>
            {/* The red pen rides in the flip bar just to the LEFT of Later, rather than on a
                row of its own — a concern is an action taken after reading the page, and
                folding it in here gives the diary back the line the old foot bar cost it. */}
            {can.report_concern && (
              <button
                type="button"
                className="btn-concern"
                onClick={() => {
                  setConcernForm(CONCERN_BLANK);
                  setConcernOpen(true);
                }}
              >
                <svg viewBox="0 0 16 16" aria-hidden="true">
                  <path d="M4.54.146A.5.5 0 0 1 4.893 0h6.214a.5.5 0 0 1 .353.146l4.394 4.394a.5.5 0 0 1 .146.353v6.214a.5.5 0 0 1-.146.353l-4.394 4.394a.5.5 0 0 1-.353.146H4.893a.5.5 0 0 1-.353-.146L.146 11.814A.5.5 0 0 1 0 11.461V5.247a.5.5 0 0 1 .146-.353L4.54.146zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
                </svg>
                {t('childDetail.diary.reportConcern')}
              </button>
            )}
            <button type="button" className="pg" disabled={activeDate >= lastDate} onClick={() => goTo(shiftDay(activeDate, 1))}>
              {t('childDetail.diary.later')} ›
            </button>
          </Box>
        )}

        {/* The red pen for the list views (Overdue / Attention), which have no flip bar to
            carry it — any of the three voices may raise a concern from whichever view they
            are reading. In day view the button rides in the flip bar above instead. */}
        {!loading && !error && view !== 'day' && can.report_concern && (
          <Box className="concernbar">
            <button
              type="button"
              className="btn-concern"
              onClick={() => {
                setConcernForm(CONCERN_BLANK);
                setConcernOpen(true);
              }}
            >
              <svg viewBox="0 0 16 16" aria-hidden="true">
                <path d="M4.54.146A.5.5 0 0 1 4.893 0h6.214a.5.5 0 0 1 .353.146l4.394 4.394a.5.5 0 0 1 .146.353v6.214a.5.5 0 0 1-.146.353l-4.394 4.394a.5.5 0 0 1-.353.146H4.893a.5.5 0 0 1-.353-.146L.146 11.814A.5.5 0 0 1 0 11.461V5.247a.5.5 0 0 1 .146-.353L4.54.146zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z" />
              </svg>
              {t('childDetail.diary.reportConcern')}
            </button>
          </Box>
        )}

        {/* Report a concern. A Dialog renders in its OWN portal, outside the diary's scoped
            stylesheet, so its content is styled inline rather than with the .sheet classes.
            Lifted above the diary layer (and its Select menus above that) for overlay mode. */}
        <Dialog
          open={concernOpen}
          onClose={saving ? undefined : () => setConcernOpen(false)}
          maxWidth="sm"
          fullWidth
          sx={{ zIndex: overlay ? 1500 : 1400 }}
          PaperProps={{ sx: { borderTop: '4px solid #A8322B', borderRadius: '10px' } }}
        >
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ fontFamily: PAPER.serif, fontSize: 19, color: '#A8322B', mb: 0.5 }}>{t('childDetail.diary.reportConcern')}</Box>
            <Box sx={{ fontSize: 12.5, color: PAPER.grey, mb: 2, lineHeight: 1.5 }}>{t('childDetail.diary.concernHint')}</Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 1.5 }}>
              <TextField
                select
                fullWidth
                size="small"
                label={t('childDetail.diary.concernWhat')}
                value={concernForm.concern_type}
                onChange={(e) => setConcernForm((f) => ({ ...f, concern_type: e.target.value }))}
                SelectProps={{ MenuProps: { sx: { zIndex: overlay ? 1550 : 1450 } } }}
              >
                {CONCERN_TYPES.map((k) => (
                  <MenuItem key={k} value={k}>
                    {t(`childDetail.diary.concernType.${k}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size="small"
                label={t('childDetail.diary.concernSeverity')}
                value={concernForm.severity}
                onChange={(e) => setConcernForm((f) => ({ ...f, severity: e.target.value }))}
                SelectProps={{ MenuProps: { sx: { zIndex: overlay ? 1550 : 1450 } } }}
              >
                {CONCERN_SEVERITIES.map((k) => (
                  <MenuItem key={k} value={k}>
                    {t(`childDetail.diary.severity.${k}`)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              label={t('childDetail.diary.concernDescribe')}
              value={concernForm.body}
              onChange={(e) => setConcernForm((f) => ({ ...f, body: e.target.value }))}
              sx={{ mb: 1.5 }}
            />
            <TextField
              fullWidth
              size="small"
              label={t('childDetail.diary.concernAction')}
              value={concernForm.action_taken}
              onChange={(e) => setConcernForm((f) => ({ ...f, action_taken: e.target.value }))}
              sx={{ mb: 1.5 }}
            />
            <Box component="label" sx={{ display: 'flex', gap: 1, alignItems: 'center', fontSize: 13.5, mb: 2, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={concernForm.response_needed}
                onChange={(e) => setConcernForm((f) => ({ ...f, response_needed: e.target.checked }))}
              />
              {t('childDetail.diary.concernResponseNeeded')}
            </Box>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Box
                component="button"
                type="button"
                onClick={() => setConcernOpen(false)}
                disabled={saving}
                sx={{
                  border: `1px solid ${PAPER.desk2}`,
                  bgcolor: '#fff',
                  borderRadius: '6px',
                  minHeight: 38,
                  px: 1.5,
                  fontSize: 13,
                  color: PAPER.ink,
                  cursor: 'pointer'
                }}
              >
                {t('childDetail.diary.cancel')}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={onReportConcern}
                disabled={saving || !concernForm.body.trim()}
                sx={{
                  border: '1px solid #A8322B',
                  bgcolor: '#A8322B',
                  color: '#fff',
                  borderRadius: '6px',
                  minHeight: 38,
                  px: 1.75,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:disabled': { opacity: 0.5, cursor: 'default' }
                }}
              >
                {t('childDetail.diary.reportIt')}
              </Box>
            </Stack>
          </Box>
        </Dialog>

        {/* Respond to a concern OR a caregiver's question — from the other side of the diary. */}
        <Dialog
          open={Boolean(replyTo)}
          onClose={saving ? undefined : () => setReplyTo(null)}
          maxWidth="sm"
          fullWidth
          sx={{ zIndex: overlay ? 1500 : 1400 }}
          PaperProps={{ sx: { borderTop: '4px solid #A8322B', borderRadius: '10px' } }}
        >
          <Box sx={{ p: 2.5 }}>
            <Box sx={{ fontFamily: PAPER.serif, fontSize: 19, color: '#A8322B', mb: 1.5 }}>
              {t(replyTo?.kind === 'note' ? 'childDetail.diary.respondQuestion' : 'childDetail.diary.respondConcern')}
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={3}
              size="small"
              label={t('childDetail.diary.yourResponse')}
              value={replyDraft}
              onChange={(e) => setReplyDraft(e.target.value)}
              sx={{ mb: 1.5 }}
            />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Box
                component="button"
                type="button"
                onClick={() => setReplyTo(null)}
                disabled={saving}
                sx={{
                  border: `1px solid ${PAPER.desk2}`,
                  bgcolor: '#fff',
                  borderRadius: '6px',
                  minHeight: 38,
                  px: 1.5,
                  fontSize: 13,
                  color: PAPER.ink,
                  cursor: 'pointer'
                }}
              >
                {t('childDetail.diary.cancel')}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={onReply}
                disabled={saving || !replyDraft.trim()}
                sx={{
                  border: '1px solid #A8322B',
                  bgcolor: '#A8322B',
                  color: '#fff',
                  borderRadius: '6px',
                  minHeight: 38,
                  px: 1.75,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:disabled': { opacity: 0.5, cursor: 'default' }
                }}
              >
                {t('childDetail.diary.recordResponse')}
              </Box>
            </Stack>
          </Box>
        </Dialog>

        <Snackbar
          open={Boolean(toast)}
          autoHideDuration={3500}
          onClose={() => setToast(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          {toast ? (
            <Alert severity={toast.severity} onClose={() => setToast(null)}>
              {toast.msg}
            </Alert>
          ) : null}
        </Snackbar>
      </Box>
    </Portal>
  );
}

CommunicationDiaryTab.propTypes = {
  childId: PropTypes.string.isRequired,
  // Present only when the diary is opened over another screen — see `overlay` above.
  onClose: PropTypes.func
};
